import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';

import { filter } from 'rxjs';
import { differenceBy } from 'lodash';
import _ from 'underscore';

import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../../Core/Users/enums/user-permissions.enum';

import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';

import { ChartMetricsEnum, MentionSeriesMetricsOnly, SocialSeriesMetricsOnly } from '../../enums/chart-metrics.enum';
import { ChartAnalysisMetricDropdownFieldModel } from '../../models/chart-analysis-metric-dropdown-field.model';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { mentionSeriesTypes, SeriesTypeEnum, socialSeriesTypes } from '../../enums/series-type.enum';
import { TermTypesEnum } from '../../../../Common/Reports/enums/term-types.enum';

@Component({
    selector: 'agpr-metric-picker',
    templateUrl: './metric-picker.component.html',
    styleUrls: ['../../../PageBuilder/components/FormFields/DropdownField/dropdown-field.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => MetricPickerComponent)
        }
    ],
    standalone: false
})
export class MetricPickerComponent extends AbstractFormFieldComponent implements OnInit, OnChanges {
    @Input() declare field: ChartAnalysisMetricDropdownFieldModel;
    @Input() declare value: ChartMetricsEnum;

    selectedOption: DropdownOptionModel = null;

    allOptions: DropdownOptionModel<ChartMetricsEnum>[] = [];
    protected currentSourceTypes: SeriesTypeEnum[] = [];
    protected hasInit: boolean = false;
    termType: TermTypesEnum;

    constructor(
        protected localizationService: LocalizationService,
        protected currentUserStateService: CurrentUserStateService) {
        super();
    }

    ngOnInit(): void {
        this.currentUserStateService.getPermissions().pipe(
            filter(x => !!x)
        ).subscribe((permissions) => {
            this.allOptions = this.field.options.slice();
            const newTypeTerm = (this.block.displayElement as ChartDisplayElementModel)?.termType;
            if (newTypeTerm === TermTypesEnum.WORDS) {
                this.allOptions = this.allOptions.filter(option => option.value === ChartMetricsEnum.VOLUME);
                this.selectedOption = this.allOptions[0];
            } else {
                this.filterOptions(
                    !permissions.includes(userPermissionsEnum.socialIntelligence),
                    !permissions.includes(userPermissionsEnum.advancedMonitoring)
                );
                if (this.value === ChartMetricsEnum.audienceReach) {
                    this.value = ChartMetricsEnum.IMPRESSIONS;
                }
            }
            this.setSelectedOption();
        });

        this.hasInit = true;
    }

    ngOnChanges() {
        // We need to filter options if the series source type changes to match metrics for each source type
        const seriesTypes = (this.block.displayElement as ChartDisplayElementModel)._series.map(s => s.type);
        const newTypeTerm = (this.block.displayElement as ChartDisplayElementModel)?.termType;

        if ((!this.hasInit || _.isEqual(seriesTypes, this.currentSourceTypes)) && (!this.termType || newTypeTerm === this.termType)) {
            return;
        }

        this.allOptions = this.field.options.slice();
        this.currentSourceTypes = seriesTypes.slice();

        if (newTypeTerm === TermTypesEnum.WORDS) {
            this.allOptions = this.allOptions.filter(option => option.value === ChartMetricsEnum.VOLUME);
            this.selectedOption = this.allOptions[0];
        } else {
            const anyMonitoring = this.currentSourceTypes.some(seriesType => mentionSeriesTypes.includes(seriesType));
            // Keep SocialSeriesMetricsOnly options available, even when the source type is not social
            this.filterOptions(false, !anyMonitoring);
        }
        this.termType = newTypeTerm;

        // Convert all old values to new ones using new available field options
        const oldMetric = this.allOptions.find(opt => opt.value === this.selectedOption?.value);
        this.selectedOption = oldMetric ?? this.allOptions[0];
        this.value = this.selectedOption.value;
        this.changes.emit(this.value);
    }

    onDropdownChanged(value: ChartMetricsEnum) {
        this.value = value;
        this.changes.emit(this.value);
    }

    setSelectedOption(): void {
        if (this.allOptions && this.allOptions.length > 0) {
            this.selectedOption = this.allOptions.find(option => option.value === this.value);

            if (!this.selectedOption) {
                this.selectedOption = this.allOptions[0];
                this.value = this.selectedOption.value;
                this.changes.emit(this.value);
            }
        }
    }

    filterOptions(excludeSocial: boolean, excludeMonitoring: boolean) {
        if (excludeSocial) {
            this.allOptions = differenceBy(this.allOptions, SocialSeriesMetricsOnly.map(metric => ({ value: metric })), 'value');
        }
        if (excludeMonitoring) {
            this.allOptions = differenceBy(this.allOptions, MentionSeriesMetricsOnly.map(metric => ({ value: metric })), 'value');
        }
    }
}
